/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { Button } from '@scuf/common';
import ChartWidget from '../../../shared/Chart/ChartWidget';
import {
  graphEnergy,
  convertDataForUtilitycost
} from '../../../shared/Utils/Utils';
import { ApiContext } from '../../../../context/ApiContext';
import BreakdownUtility from '../../../shared/BreakdownUtility/BreakdownUtility';
import {
  IConvertDataForUtilityTrendChart,
  IUtilityBillData
} from '../../IEnergyManagementProps';
import ProgressBarWidget from '../../../shared/ProgressBar/ProgressBarWidget';
import { IDataTableHeader } from '../../../shared/BreakdownByMeter/BreakdownByMeterProps';
import { getUtilityTableHeader } from '../../../shared/Utils/UtilityViewDetails';
import { EnergyUsageField } from '../../../shared/Enums';
import { getProgressBarUnitScaledData } from '../../../shared/Utils/portfolioDataFormatter';
import { PERCENT_CHANGE_THRESHOLD } from '../../../shared/Utils/Constants';
interface IUtilityBillComponentProps {
  data: IUtilityBillData;
}

export const UtilityComponentSite = (props: IUtilityBillComponentProps) => {
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const { data } = props;

  const apiContext = useContext(ApiContext);
  const { onUtilityBillViewDetailsClick, isEOSubscribed } = apiContext;

  const onViewDetailsClick = () => {
    if (onUtilityBillViewDetailsClick) {
      onUtilityBillViewDetailsClick();
    }
  };

  const {
    utilityCostCurrency,
    totalAggregations: {
      totalActualCost,
      previousPeriodCost,
      totalCostSavingsPercentage,
      totalCostSavings,
      totalActualConsumption,
      previousPeriodConsumption
    },
    breakdownByUtility,
    monthwiseAggregations
  } = data;
  const savingPercent =
    apiContext.showChangeWhenData &&
    (isNaN(Number(totalActualCost)) ||
      isNaN(Number(previousPeriodCost)) ||
      Number(totalActualCost) === 0 ||
      Number(previousPeriodCost) === 0 ||
      Math.abs(Number(totalCostSavingsPercentage)) > PERCENT_CHANGE_THRESHOLD);

  const aggregationEnergyData = {
    actual: totalActualCost,
    previousPeriod: previousPeriodCost,
    savings: savingPercent ? null : totalCostSavings,
    savingsPercentage: savingPercent ? null : totalCostSavingsPercentage,
    ActualConsumption: totalActualConsumption,
    previousPeriodConsumption: previousPeriodConsumption
  };
  const headers: IDataTableHeader[] = getUtilityTableHeader(t);

  const UtilityData = convertDataForUtilitycost(
    breakdownByUtility,
    apiContext.showChangeWhenData
  );

  const graphData = graphEnergy(
    (monthwiseAggregations as unknown) as IConvertDataForUtilityTrendChart[],
    undefined,
    lng
  );
  const { YearIndex, MonthIndex } = graphData;

  let { 0: first, [MonthIndex.length - 1]: last } = MonthIndex;
  let { 0: previousyear, [YearIndex.length - 1]: currentyear } = YearIndex;
  return (
    <div className="kpi-card-flex-container">
      <div className="kpi-card-progress-bar-container">
        <ProgressBarWidget
          category={EnergyUsageField.UTILITY}
          aggregationData={aggregationEnergyData}
          utilityCostCurrency={utilityCostCurrency}
        />
      </div>
      <div className="kpi-card-monthwise-container">
        {!isEOSubscribed && (
          <>
            {previousyear != currentyear ? (
              <div className="kpi-card-monthwise-date-range">
                {t(d.Utilitycost)} {first} {previousyear} - {last} {currentyear}
              </div>
            ) : (
              <div className="kpi-card-monthwise-date-range">
                {t(d.Utilitycost)} {first} - {last} {currentyear}
              </div>
            )}

            <ChartWidget
              graphData={graphData}
              unit={utilityCostCurrency}
              axisUnitType="currency"
            />
          </>
        )}
        <div className="kpi-card-view-details">
          <Button
            type="link"
            content={t(d.VIEWDETAILS)}
            className="view-details-button"
            onClick={() => onViewDetailsClick()}
          />
        </div>
      </div>

      <div className="kpi-card-meter-container">
        <BreakdownUtility
          columnHeaders={headers as IDataTableHeader[]}
          meterData={UtilityData}
          utilityCostCurrency={utilityCostCurrency}
        />
      </div>
    </div>
  );
};
export default memo(UtilityComponentSite);
